/**
 * VENDORED — sync with tara-client copy (P3: tara-client/src/lib/viewer-bridge.ts).
 *
 * Typed postMessage bridge between the portal shell and the sandboxed report
 * iframe. The iframe has an opaque origin (sandbox without allow-same-origin)
 * so messages are matched on `event.source` + a `source` marker field, never
 * on origin.
 *
 * P3 adds the EDIT-mode payloads: the viewer posts {type:'edit-state'} as the
 * user types and {type:'serialized'} on request; the host posts
 * {type:'request-serialize'} and {type:'revert-all'}. These are mirrored in
 * the tara-portal copy so the two files stay byte-identical.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

// ---------- message types (discriminated unions) ----------

export interface SlideMessage {
  source: 'tara-viewer';
  type: 'slide';
  index: number;
  total: number;
}

export interface CompatMessage {
  source: 'tara-viewer';
  type: 'compat';
  slides: number;
  warnings: string[];
}

export interface ExpandMessage {
  source: 'tara-viewer';
  type: 'expand';
  id: number;
  expanded: boolean;
}

export interface LinkMessage {
  source: 'tara-viewer';
  type: 'link';
  href: string;
}

export interface EditStateMessage {
  source: 'tara-viewer';
  type: 'edit-state';
  /** Number of editable leaves whose text differs from the loaded version. */
  dirty: number;
  /** Total number of editable text leaves discovered in the report. */
  editable: number;
}

export interface SerializedMessage {
  source: 'tara-viewer';
  type: 'serialized';
  /** Full `<!doctype html>…` of the edited document (raw; host strips it). */
  html: string;
  dirty: number;
}

export type ViewerToHostMessage =
  | SlideMessage
  | CompatMessage
  | ExpandMessage
  | LinkMessage
  | EditStateMessage
  | SerializedMessage;

export interface GotoMessage {
  source: 'tara-portal';
  type: 'goto';
  index: number;
}

export interface RequestSerializeMessage {
  source: 'tara-portal';
  type: 'request-serialize';
}

export interface RevertAllMessage {
  source: 'tara-portal';
  type: 'revert-all';
}

export type HostToViewerMessage =
  | GotoMessage
  | RequestSerializeMessage
  | RevertAllMessage;

// ---------- parent-side hook ----------

export interface ViewerBridgeState {
  index: number;
  total: number;
  compat: CompatMessage | null;
  editState: { dirty: number; editable: number } | null;
}

export interface ViewerBridge extends ViewerBridgeState {
  goto: (index: number) => void;
  next: () => void;
  prev: () => void;
  /** Ask the iframe to serialize its current (edited) document. */
  requestSerialize: () => void;
  /** Revert every unsaved edit in the iframe back to the loaded version. */
  revertAll: () => void;
}

function isViewerMessage(data: unknown): data is ViewerToHostMessage {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as { source?: unknown }).source === 'tara-viewer' &&
    typeof (data as { type?: unknown }).type === 'string'
  );
}

export function useViewerBridge(
  iframeRef: RefObject<HTMLIFrameElement>,
  handlers?: {
    onExpand?: (msg: ExpandMessage) => void;
    onSerialized?: (msg: SerializedMessage) => void;
  }
): ViewerBridge {
  const [state, setState] = useState<ViewerBridgeState>({
    index: 0,
    total: 0,
    compat: null,
    editState: null,
  });

  // The message listener below is installed once (deps: [iframeRef]) so it
  // isn't torn down/re-added on every render. But `handlers.onExpand` /
  // `handlers.onSerialized` change identity every render (they close over live
  // state — e.g. the workbench's `saving` flag). Reading them straight from the
  // effect closure would freeze them at mount, so an edit-mode Save would fire
  // the STALE onSerialized (saving===false) and silently no-op. Keep a ref that
  // every render refreshes, and read the current handlers through it.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const data: unknown = event.data;
      if (!isViewerMessage(data)) return;
      switch (data.type) {
        case 'slide':
          setState((s) => ({ ...s, index: data.index, total: data.total }));
          break;
        case 'compat':
          setState((s) => ({ ...s, total: data.slides, compat: data }));
          break;
        case 'expand':
          handlersRef.current?.onExpand?.(data);
          break;
        case 'edit-state':
          setState((s) => ({
            ...s,
            editState: { dirty: data.dirty, editable: data.editable },
          }));
          break;
        case 'serialized':
          handlersRef.current?.onSerialized?.(data);
          break;
        case 'link': {
          // Vet before opening: http(s) only, opened detached from us.
          if (/^https?:\/\//i.test(data.href)) {
            window.open(data.href, '_blank', 'noopener,noreferrer');
          }
          break;
        }
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef]);

  const send = useCallback(
    (message: HostToViewerMessage) => {
      // '*' is required: the sandboxed iframe's origin is opaque ("null").
      iframeRef.current?.contentWindow?.postMessage(message, '*');
    },
    [iframeRef]
  );

  const goto = useCallback(
    (index: number) => {
      send({ source: 'tara-portal', type: 'goto', index });
    },
    [send]
  );

  return {
    ...state,
    goto,
    next: useCallback(() => goto(state.index + 1), [goto, state.index]),
    prev: useCallback(() => goto(state.index - 1), [goto, state.index]),
    requestSerialize: useCallback(
      () => send({ source: 'tara-portal', type: 'request-serialize' }),
      [send]
    ),
    revertAll: useCallback(
      () => send({ source: 'tara-portal', type: 'revert-all' }),
      [send]
    ),
  };
}
